/**
 * trønso Portemonnaie-Animation: eine Szene aus zwei freigestellten Ebenen, deren
 * Bewegung am Scroll-Fortschritt der Section hängt. Erst liegt der Cardholder an
 * seinem Platz am aufgeklappten Portemonnaie, dann zieht das Base-Wallet nach
 * rechts aus dem Bild und legt die vier Druckknöpfe frei, am Ende steht der
 * Cardholder allein: kein Autoplay, die Bewegung gehört dem Nutzer.
 *
 * Gestaltungsentscheidungen:
 * - Gerechnet wird hier nur eine einzige Zahl: `--tronso-animation-detach` (0…1).
 *   Wohin sich was bewegt, steht komplett im CSS der Section. Das hält die beiden
 *   Tempi auseinander, die es hier gibt: das Scrubben am Finger (diese Zahl) und
 *   die Choreografie der Phasen (CSS-`clamp` auf derselben Zahl).
 * - Ohne JS bleibt `detach` bei 0: es steht das vollständige Portemonnaie, die
 *   Section ist also nie leer.
 * - Bei prefers-reduced-motion wird gar nicht gescrubbt: Die Section verliert per
 *   CSS ihre Überhöhe, das Portemonnaie steht vollständig da, die Scroll-Punkte
 *   stehen alle untereinander.
 * - Beide Ebenen laden nur dann eager und mit Vorrang, wenn die Section ganz oben
 *   steht (siehe `image_loading` in der Section). Darunter laden sie lazy, damit
 *   sie dem Hero keine Bandbreite nehmen.
 * - Gerechnet wird nur in requestAnimationFrame, geschrieben nur bei echter
 *   Änderung.
 * - Eine zweite Zahl kommt beim Messen dazu: `--tronso-canvas-zoom-cap`, der
 *   Deckel für die Endgröße des Cardholders. Er steht als Pixelwert in den
 *   Einstellungen, die Skalierung im CSS braucht aber einen Faktor, und CSS kann
 *   eine Länge nicht durch eine Länge teilen. Also wird hier gemessen und
 *   geteilt: nur beim Größenwechsel, nicht beim Scrollen.
 *
 * Wichtig: Ab 990 px Breite scrollt in Atelier nicht das Fenster, sondern
 * `.page-wrapper` (`height: 100dvh; overflow-y: auto`, html/body auf `overflow: hidden`).
 * Ein Listener auf `window` bekommt dort nie ein Scroll-Ereignis. Deshalb laufen
 * Listener und Observer über `@theme/scroll-container`: dieselben Helfer, die auch
 * `header.js` benutzt, und werden beim Breakpoint-Wechsel neu gesetzt.
 */
import { Component } from '@theme/component';
import { clamp, prefersReducedMotion } from '@theme/utilities';
import { getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Haltezone am Ende: Anteil des Scroll-Wegs, in dem die Szene schon fertig ist und
 * das Schlussbild ruhig stehen bleibt.
 *
 * Am Anfang gibt es bewusst **keine** Haltezone mehr (Ansage des Users am
 * 2026-09-02: "es sollte sofort scrollen"). Vorher standen dort 8 % des Scroll-Wegs
 * still, auf die sich `detach_start` noch einmal obendrauf gelegt hat. Zusammen war
 * das rund eine Bildschirmhöhe Scrollen, in der sich nichts bewegt hat, während die
 * Bühne schon festgepinnt war: Es fühlte sich an, als hänge die Seite fest. Der
 * Anfangszustand braucht die Haltezone auch nicht, er ist beim Einfahren der Section
 * lange genug zu sehen.
 */
const HOLD_END = 0.08;

/** Kleinste Änderung, die einen Schreibzugriff wert ist. */
const EPSILON = 0.001;

/**
 * Anteil des letzten Punkt-Abschnitts, in dem der Hintergrund wegfährt. Der Rest
 * des Abschnitts gehört dem fertigen Schlussbild: Der Hintergrund soll weg sein,
 * während der Text noch zu lesen ist, nicht unter ihm weglaufen.
 */
const OUTRO_SHARE = 0.45;

/**
 * @typedef {object} TronsoAnimationRefs
 * @property {HTMLElement} sticky - Der im Viewport stehenbleibende Rahmen.
 * @property {HTMLElement[]} [point] - Die gestapelten Scroll-Punkte in Reihenfolge.
 * @property {HTMLImageElement} [cardholder] - Die Ebene, deren Endgröße gedeckelt wird.
 *
 * @extends {Component<TronsoAnimationRefs>}
 */
export class TronsoAnimation extends Component {
  requiredRefs = ['sticky'];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {EventTarget | undefined} */
  #scrollTarget;
  /** @type {HTMLElement[]} */
  #points = [];
  /** Zuletzt aktiver Scroll-Punkt. */
  #point = 0;
  /** Zuletzt geschriebener Fortschritt, -1 = noch nichts geschrieben. */
  #progress = -1;
  /** Anteil des Scroll-Wegs, bis zu dem die Szene stillsteht. */
  #detachStart = 0;
  /** Größte Breite, die der Cardholder am Ende annehmen darf, in px. 0 = kein Deckel. */
  #cardholderMax = 0;
  /** Abstand, bei dem die Bühne festpinnt (die Kopfzeilenhöhe), in px. */
  #pinOffset = 0;
  /** Steht an, solange der Deckel noch nicht zur aktuellen Größe passt. */
  #needsMeasure = true;
  #frameRequest = 0;
  /** @type {IntersectionObserver | undefined} */
  #loadObserver;

  /**
   * Setzt die Bildadressen der Ebenen, die die Section in `data-tronso-*` geparkt hat
   * (nur wenn sie nicht ganz oben steht, siehe `defer_layers` in der Section).
   *
   * Gestartet wird, sobald eines von beiden eintritt: Die Seite ist fertig geladen
   * (dann bekommt das Produktbild oben die Leitung allein und die Szene laedt
   * danach im Hintergrund), oder die Section kommt bis auf eine Bildschirmhoehe
   * heran (wer schnell scrollt, wartet nicht auf das Ladeende). Die Rueckseite
   * (`data-tronso-spaet`) folgt erst, wenn Vorderseite und Base-Wallet da sind: Sie
   * wird erst am Ende des Scroll-Wegs gebraucht.
   */
  #loadLayers() {
    /** @type {HTMLImageElement[]} */
    const pending = Array.from(this.querySelectorAll('img[data-tronso-src]'));
    if (pending.length === 0) return;

    const early = pending.filter((img) => !img.hasAttribute('data-tronso-spaet'));
    const late = pending.filter((img) => img.hasAttribute('data-tronso-spaet'));

    /** @param {HTMLImageElement} img */
    const show = (img) => {
      const { tronsoSizes, tronsoSrcset, tronsoSrc } = img.dataset;
      if (tronsoSizes) img.sizes = tronsoSizes;
      if (tronsoSrcset) img.srcset = tronsoSrcset;
      if (tronsoSrc) img.src = tronsoSrc;
      img.removeAttribute('data-tronso-sizes');
      img.removeAttribute('data-tronso-srcset');
      img.removeAttribute('data-tronso-src');
      return new Promise((resolve) => {
        if (img.complete) return resolve(undefined);
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    };

    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      this.#loadObserver?.disconnect();
      this.#loadObserver = undefined;
      Promise.all(early.map(show)).then(() => late.forEach(show));
    };

    const whenIdle = () =>
      'requestIdleCallback' in window ? requestIdleCallback(start, { timeout: 1500 }) : setTimeout(start, 200);

    if (document.readyState === 'complete') whenIdle();
    else window.addEventListener('load', whenIdle, { once: true });

    this.#loadObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) start();
      },
      { rootMargin: '100% 0px' }
    );
    this.#loadObserver.observe(this);
  }

  connectedCallback() {
    super.connectedCallback();

    // Die Scroll-Punkte sind optional: eine Section ohne Bloecke zeigt nur die Szene.
    const { point } = this.refs;
    this.#points = point ? (Array.isArray(point) ? point : [point]) : [];

    const detachStart = Number(this.dataset.detachStart) / 100;
    this.#detachStart = detachStart > 0 && detachStart < 1 ? detachStart : 0;

    const cardholderMax = Number(this.dataset.cardholderMax);
    this.#cardholderMax = cardholderMax > 0 ? cardholderMax : 0;

    // Vor der Abfrage auf reduzierte Bewegung: Die Bilder braucht auch das Standbild.
    this.#loadLayers();

    // Reduzierte Bewegung: kein Scrubben. `--tronso-animation-detach` bleibt ungesetzt
    // und faellt im CSS auf 0 zurueck: das vollstaendige Portemonnaie. Die Punkte
    // stehen dann per CSS alle untereinander, es braucht keine Klasse.
    if (prefersReducedMotion()) return;

    this.#resizeObserver = new ResizeObserver(this.#remeasure);
    this.#resizeObserver.observe(this);

    this.#listenToScroll();
    scrollContainerMediaQuery.addEventListener('change', this.#switchScrollContainer);
    window.addEventListener('resize', this.#remeasure, { passive: true });

    // Erst messen, dann zeichnen: Ohne den Pin-Abstand stünde der erste Frame auf
    // einem leicht zu kleinen Fortschritt, wenn die Seite mitten in der Section lädt.
    this.#measure();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#scrollTarget?.removeEventListener('scroll', this.#schedule);
    this.#scrollTarget = undefined;
    scrollContainerMediaQuery.removeEventListener('change', this.#switchScrollContainer);
    window.removeEventListener('resize', this.#remeasure);
    this.#resizeObserver?.disconnect();
    this.#loadObserver?.disconnect();
    cancelAnimationFrame(this.#frameRequest);
    this.#frameRequest = 0;
  }

  /** Haengt den Scroll-Listener an den Container, der tatsaechlich scrollt. */
  #listenToScroll() {
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#schedule, { passive: true });
  }

  /**
   * Beim Wechsel ueber 990 px wandert das Scrollen zwischen Dokument und
   * `.page-wrapper`. Der Listener muss dann umziehen, sonst laeuft die Animation
   * nach einem Fensterwechsel ins Leere.
   */
  #switchScrollContainer = () => {
    this.#scrollTarget?.removeEventListener('scroll', this.#schedule);
    this.#listenToScroll();
    this.#schedule();
  };

  /** Bündelt Scroll- und Resize-Ereignisse auf je einen Frame. */
  #schedule = () => {
    if (this.#frameRequest) return;
    this.#frameRequest = requestAnimationFrame(() => {
      this.#frameRequest = 0;
      if (this.#needsMeasure) this.#measure();
      this.#update();
    });
  };

  /** Wie #schedule, meldet aber zusätzlich den Deckel als veraltet an. */
  #remeasure = () => {
    this.#needsMeasure = true;
    this.#schedule();
  };

  /**
   * Rechnet den Pixel-Deckel aus den Einstellungen in einen Skalierungsfaktor um.
   * Gemessen wird die *Layout*breite des Cardholders, also seine Breite vor der
   * Skalierung: `offsetWidth` ignoriert Transformationen. Der Faktor ist damit
   * genau das, was die Ebene am Ende höchstens breit werden darf, geteilt durch
   * das, was sie am Anfang ist. Er gilt für beide Bühnen (Desktop und Handy),
   * weil er aus der gemessenen Lage kommt und nicht aus einer Rechnung im Liquid.
   *
   * Nur beim Größenwechsel, nicht beim Scrollen: Ein Lesen von `offsetWidth`
   * erzwingt ein Layout, und die Breite ändert sich beim Scrollen nicht.
   */
  #measure() {
    this.#needsMeasure = false;

    /*
      Der Pin-Abstand steht im CSS als `inset-block-start: var(--header-height)` und
      ändert sich mit der Kopfzeile (Breakpoint, ein- oder ausgeblendete
      Ankündigungsleiste). Deshalb wird er hier mitgemessen und nicht im Liquid
      gerechnet. `getComputedStyle` liefert den aufgelösten Pixelwert; `auto` (falls
      die Bühne einmal nicht klebt) ergibt NaN und fällt auf 0 zurück.
    */
    const sticky = this.refs.sticky;
    if (sticky) {
      const pinOffset = Number.parseFloat(getComputedStyle(sticky).insetBlockStart);
      this.#pinOffset = Number.isFinite(pinOffset) ? pinOffset : 0;
    }

    const cardholder = this.refs.cardholder;
    if (!this.#cardholderMax || !cardholder) return;

    const base = cardholder.offsetWidth;
    if (!base) return;

    // Nie unter 1: Der Deckel begrenzt das Wachsen, er darf nicht schrumpfen.
    this.style.setProperty('--tronso-canvas-zoom-cap', String(Math.max(1, this.#cardholderMax / base)));
  }

  #update() {
    const sticky = this.refs.sticky;
    if (!sticky) return;

    const rect = this.getBoundingClientRect();
    const scrollable = rect.height - sticky.offsetHeight;

    /*
      Gerechnet wird ab dem Punkt, an dem die Bühne festpinnt, nicht ab dem oberen
      Fensterrand: Die Bühne klebt an `--header-height` (siehe `inset-block-start` im
      CSS), steht dort also schon fest, während `rect.top` noch positiv ist. Ohne den
      Versatz wären genau diese Pixel tote Strecke: Bild steht, Fortschrittslinie
      steht, gescrollt wird trotzdem.
    */
    const raw = scrollable > 0 ? clamp((this.#pinOffset - rect.top) / scrollable, 0, 1) : 0;

    // Nur noch die Haltezone am Ende herausrechnen, Rest linear auf 0…1 strecken.
    const progress = clamp(raw / (1 - HOLD_END), 0, 1);
    if (Math.abs(progress - this.#progress) < EPSILON) return;
    this.#progress = progress;

    this.style.setProperty('--tronso-animation-progress', String(progress));

    // Die Ablösung bleibt linear: eine Beschleunigungskurve wuerde sich beim
    // Scrollen wie Verzoegerung anfuehlen, weil die Bewegung am Finger haengt.
    // Die Staffelung der Phasen (erst Base-Wallet, dann Cardholder) macht das CSS.
    const detach = this.#detachStart
      ? clamp((progress - this.#detachStart) / (1 - this.#detachStart), 0, 1)
      : progress;
    this.style.setProperty('--tronso-animation-detach', String(detach));
    this.style.setProperty('--tronso-animation-outro', String(this.#outro(progress)));

    this.#paintPoint(progress);
  }

  /**
   * Fortschritt des Abgangs: Sobald der letzte Scroll-Punkt an der Reihe ist,
   * faehrt der Hintergrund der Section nach oben weg und die Schlussszene steht
   * auf dem blanken Grundton. Ansage des Users am 2026-08-30: "sobald der text
   * der vorher in der scrollsection war durch ist" soll es aussehen wie die
   * fruehere eigene Section darunter.
   *
   * Die Schwelle wird aus der Anzahl der Punkte gerechnet und nicht als Setting
   * gepflegt: Die Punkte teilen sich den Scroll-Weg zu gleichen Teilen, also faengt
   * der letzte immer bei (n-1)/n an. Wer einen Punkt hinzufuegt oder wegnimmt, muss
   * so nichts nachziehen. Der Abgang selbst braucht nur den vorderen Teil dieses
   * Abschnitts, damit der Rest als ruhiges Schlussbild stehen bleibt.
   *
   * @param {number} progress - Fortschritt der Section, 0…1.
   * @returns {number} 0…1
   */
  #outro(progress) {
    const count = this.#points.length;
    const start = count > 1 ? (count - 1) / count : 0.8;
    return clamp((progress - start) / ((1 - start) * OUTRO_SHARE), 0, 1);
  }

  /**
   * Blendet den Punkt ein, der zum aktuellen Abschnitt gehoert. Die Bloecke teilen
   * sich den Scroll-Weg zu gleichen Teilen: wer einen Punkt hinzufuegt, muss keine
   * Schwellen nachpflegen.
   *
   * @param {number} progress - Fortschritt der Section, 0…1.
   */
  #paintPoint(progress) {
    const count = this.#points.length;
    if (count === 0) return;

    const index = Math.min(count - 1, Math.floor(progress * count));
    if (index === this.#point) return;

    this.#points[this.#point]?.classList.remove('is-active');
    this.#points[index]?.classList.add('is-active');
    this.#point = index;
  }
}

if (!customElements.get('tronso-animation')) {
  customElements.define('tronso-animation', TronsoAnimation);
}
